import { augmentDataContextColumn } from '../augment-datacontext';
import { convertCategoricalColumn } from '../init-data/helpers';
import { updateCanonicalTypeRestrictionData } from '../analyticsDataStorage';
import { TpCanonicalTypeInfo, TpDataContext, TpDataTable, TpDerivedColumnPatch } from '../interfaces';
import { createEmptyEnSelectionFilter } from '../selection/selectionEnFilters';
import { MUTATION_LEDGER_VERSION } from './schemas';
import type { LedgerAction } from './schemas';

/** Validate that an add/update ledger action describes the patches it records. */
export function validateLedgerActionAgainstPatches(
  ledgerAction: Pick<LedgerAction, 'targetTableId' | 'targetColumnIds'>,
  patches: TpDerivedColumnPatch[],
): void {
  if (patches.length === 0) throw new Error('Ledger action has no patches');
  if (patches.some((patch) => ledgerAction.targetTableId !== patch.tableId))
    throw new Error('Ledger action target table does not match patches');

  const patchColumnIds = new Set(patches.map((patch) => patch.columnId));
  if (
    ledgerAction.targetColumnIds.length !== patchColumnIds.size ||
    ledgerAction.targetColumnIds.some((columnId) => !patchColumnIds.has(columnId))
  )
    throw new Error('Ledger action target columns do not match patches');
}

/**
 * Validate a complete derived-column patch batch before any table is mutated.
 *
 * Base columns and groups are immutable. Existing derived columns may be
 * recomputed, but their metadata must remain unchanged across replays.
 */
export function validateDerivedColumnPatches(dataContext: TpDataContext, patches: TpDerivedColumnPatch[]): void {
  for (const patch of patches) {
    const dataTable = dataContext.dataTables.find((table) => table.id === patch.tableId);
    if (!dataTable) throw new Error(`Datatable not found: ${patch.tableId}`);

    if (dataTable.baseColumnIds?.includes(patch.columnId)) {
      throw new Error(`Cannot modify base column ${patch.columnId} in datatable ${patch.tableId}`);
    }

    const existingColumn = dataTable.columns.find((column) => column.id === patch.columnId);
    if (existingColumn) {
      const expectedDecimalDigits = patch.decimalDigits ?? 3;
      if (
        existingColumn.name !== patch.columnName ||
        (existingColumn.description ?? null) !== (patch.columnDescription ?? null) ||
        existingColumn.valueType !== patch.valueType ||
        (existingColumn.groupId ?? null) !== (patch.groupId ?? null) ||
        (existingColumn.decimalDigits ?? 3) !== expectedDecimalDigits
      ) {
        throw new Error(`Cannot change metadata for derived column ${patch.columnId}`);
      }
    }
  }
}

/** Apply a validated patch batch using only ordinary object and array mutations. */
export function applyDerivedColumnPatches(
  dataContext: TpDataContext,
  patches: TpDerivedColumnPatch[],
  ledgerAction?: LedgerAction,
): void {
  for (const patch of patches) {
    const dataTable = dataContext.dataTables.find((table) => table.id === patch.tableId);
    if (!dataTable) throw new Error(`Datatable not found: ${patch.tableId}`);

    const groupId = patch.groupId;
    if (groupId && dataTable.columnGroups.every((group) => group.id !== groupId)) {
      dataTable.columnGroups.push({
        id: groupId,
        name: patch.groupName || groupId,
        description: patch.groupDescription,
      });
    }

    if (!dataTable.columnData[patch.columnId]) {
      dataTable.columns.push({
        id: patch.columnId,
        name: patch.columnName,
        description: patch.columnDescription,
        valueType: patch.valueType,
        decimalDigits: patch.decimalDigits ?? 3,
        groupId,
        augmentedData: {},
      });
    }

    const column = dataTable.columns.find((candidate) => candidate.id === patch.columnId);
    if (!column) throw new Error(`Invalid column ${patch.columnId} for datatable ${patch.tableId}`);
    dataTable.columnData[patch.columnId] = patch.columnData;
    convertCategoricalColumn(dataContext, dataTable, column);
    augmentDataContextColumn(dataContext, dataTable, column);
  }

  if (ledgerAction) {
    dataContext.mutationLedger.actions.push(ledgerAction);
  }
}

function shallowResetTable(table: TpDataTable): TpDataTable {
  if (!table.baseColumnIds || !table.baseColumnGroupIds) {
    throw new Error(`Cannot create replay shadow for datatable ${table.id}: base identifiers are missing`);
  }

  const baseColumnIds = new Set(table.baseColumnIds);
  const baseColumnGroupIds = new Set(table.baseColumnGroupIds);
  return {
    ...table,
    columns: table.columns.filter((column) => baseColumnIds.has(column.id)),
    columnGroups: table.columnGroups.filter((group) => baseColumnGroupIds.has(group.id)),
    columnData: Object.fromEntries(
      Object.entries(table.columnData).filter(([columnId]) => baseColumnIds.has(columnId)),
    ),
  };
}

function resetCanonicalTypeForReplay(canonicalType: TpCanonicalTypeInfo): TpCanonicalTypeInfo {
  const replayCanonicalType = {
    ...canonicalType,
    selectedItemsMap: null,
    selectionFilter: createEmptyEnSelectionFilter(),
    restrictionFilter: null,
    restrictedItemsMap: null,
  };
  updateCanonicalTypeRestrictionData(replayCanonicalType);
  return replayCanonicalType;
}

/** Build a cheap replay target from base data with no live selection state. */
export function createShadowDataContext(dataContext: TpDataContext): TpDataContext {
  const canonicalTypes: { [id: string]: TpCanonicalTypeInfo } = Object.fromEntries(
    Object.entries(dataContext.canonicalTypes).map(([id, canonicalType]) => [
      id,
      resetCanonicalTypeForReplay(canonicalType),
    ]),
  );

  return {
    ...dataContext,
    dataTables: dataContext.dataTables.map(shallowResetTable),
    canonicalTypes,
    mutationLedger: { version: MUTATION_LEDGER_VERSION, actions: [] },
  };
}
